import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import {
  Button as AntdButton,
  Col,
  Row,
  ButtonProps as AntdButtonProps,
  message,
  Select,
  Spin,
  Table,
  Descriptions,
  Tooltip,
  DatePicker,
  InputNumber,
} from "antd";
import { RiFileEditLine } from "react-icons/ri";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import EditablePickSequence from "../../table-components/editable-pick-sequence";
import { IQuantity } from "../../../../models/product/response";
import EndPoints from "../../../../services/end-points";
import { IBatch } from "../../../../models/wms/batch/response";
import dayjs from "dayjs";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
}

// Helper function to get display name from store_name
const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === "string") return name;
  return name?.ar || name?.en || "-";
};

const formatStorableLocation = (storable: IBatch["storable"]): string => {
  if (!storable) return "-";
  const parts = [
    storable.zone?.name,
    storable.aisle?.name,
    storable.storageUnit?.name,
    storable.shelf?.name,
    storable.name,
  ].filter(Boolean) as string[];

  // If backend doesn't send the hierarchy, fallback to the storable name.
  if (parts.length === 0) return storable.name ?? "-";
  return parts.join(" / ");
};

interface IBatchRow extends IBatch {
  editedEntered: number;
  editedWithdrawn: number;
  editedExpirationDate: string;
  dirty: boolean;
  saving: boolean;
}

const MatchQuantityButton: React.FC<ButtonProps> = ({
  passedId,
  quantities,
  ...props
}) => {
  const { t } = useTranslation();
  const { actions, loading } = useContext(ProductContext);

  const [showFormModal, hideFormModal] = useModal(() => {
    const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [rows, setRows] = useState<IBatchRow[]>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const tableWrapRef = useRef<HTMLDivElement>(null);
    const [tableScrollY, setTableScrollY] = useState(280);

    const storeQuantity = quantities.find((q) => q.store_id === selectedStoreId);

    useLayoutEffect(() => {
      const el = tableWrapRef.current;
      if (!el) return;
      const measure = () => {
        const h = el.clientHeight;
        setTableScrollY(Math.max(120, Math.floor(h - 112)));
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [selectedStoreId, loadingBatches, rows.length]);

    const loadBatches = async (opts?: { storeId?: number; page?: number; perPage?: number }) => {
      const storeId = opts?.storeId ?? selectedStoreId;
      const nextPage = opts?.page ?? page;
      const nextPerPage = opts?.perPage ?? perPage;
      if (!storeId) return;

      setLoadingBatches(true);
      try {
        const { data } = await EndPoints.wmsBatch.getAllBatches({
          productId: passedId,
          storeId,
          page: nextPage,
          perPage: nextPerPage,
        });
        const batches: IBatch[] = data?.data ?? [];
        setTotal(data?.total ?? 0);
        setPage(data?.currentPage ?? nextPage);
        setRows(
          batches.map((b) => ({
            ...b,
            editedEntered: b.enteredQuantity,
            editedWithdrawn: Math.abs(b.withdrawnQuantity),
            editedExpirationDate: b.expirationDate ?? "",
            dirty: false,
            saving: false,
          }))
        );
      } catch {
        // silent
      } finally {
        setLoadingBatches(false);
      }
    };

    const handleStoreChange = async (storeId: number) => {
      setSelectedStoreId(storeId);
      setRows([]);
      setTotal(0);
      setPage(1);
      if (!storeId) return;
      await loadBatches({ storeId, page: 1 });
    };

    const updateRow = (
      id: number,
      field: "editedEntered" | "editedWithdrawn" | "editedExpirationDate",
      value: string
    ) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const next = { ...r, [field]: field === "editedExpirationDate" ? value : Number(value) };
          next.dirty =
            next.editedEntered !== r.enteredQuantity ||
            next.editedWithdrawn !== Math.abs(r.withdrawnQuantity) ||
            next.editedExpirationDate !== (r.expirationDate ?? "");
          return next;
        })
      );
    };

    const saveBatch = async (row: IBatchRow) => {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, saving: true } : r))
      );
      try {
        await EndPoints.wmsBatch.updateBatch(row.id, {
          enteredQuantity: row.editedEntered,
          withdrawnQuantity: -Math.abs(row.editedWithdrawn),
          expirationDate: row.editedExpirationDate || undefined,
        });
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  enteredQuantity: row.editedEntered,
                  withdrawnQuantity: row.editedWithdrawn,
                  availableQuantity: row.editedEntered + row.editedWithdrawn,
                  expirationDate: row.editedExpirationDate || undefined,
                  dirty: false,
                  saving: false,
                }
              : r
          )
        );
      } catch {
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, saving: false } : r))
        );
      }
    };

    const handleClose = () => {
      actions.getData();
      hideFormModal();
    };

    const columns = [
      {
        title: t("id"),
        dataIndex: "id",
        key: "id",
        width: 72,
        align: "center" as const,
      },
      {
        title: t("current_location"),
        key: "location",
        width: 220,
        align: "center" as const,
        onCell: () => ({
          style: { overflow: "hidden", maxWidth: 220, verticalAlign: "middle" },
        }),
        render: (_: any, record: IBatchRow) => {
          const text = formatStorableLocation(record.storable);
          return (
            <Tooltip title={text}>
              <div
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  margin: "0 auto",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: t("expiration_date"),
        key: "expirationDate",
        width: 148,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <DatePicker
            size="small"
            value={record.editedExpirationDate ? dayjs(record.editedExpirationDate) : null}
            onChange={(_date, dateString) =>
              updateRow(record.id, "editedExpirationDate", dateString as string)
            }
          />
        ),
      },
      {
        title: t("entered_quantity"),
        key: "enteredQuantity",
        width: 112,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <EditablePickSequence
            value={record.editedEntered}
            onSave={async (val) => {
              updateRow(record.id, "editedEntered", String(val));
            }}
          />
        ),
      },
      {
        title: t("withdrawn_quantity"),
        key: "withdrawnQuantity",
        width: 112,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <InputNumber
            size="small"
            min={0}
            style={{ width: 80 }}
            value={record.editedWithdrawn}
            onChange={(val) =>
              updateRow(record.id, "editedWithdrawn", String(val ?? 0))
            }
          />
        ),
      },
      {
        title: t("available"),
        key: "available",
        width: 96,
        align: "center" as const,
        render: (_: any, record: IBatchRow) =>
          record.editedEntered - record.editedWithdrawn,
      },
      {
        title: "",
        key: "action",
        width: 100,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <AntdButton
            size="small"
            type="primary"
            loading={record.saving}
            disabled={!record.dirty}
            onClick={() => saveBatch(record)}
          >
            {t("save")}
          </AntdButton>
        ),
      },
    ];

    return (
      <FormHookModal
        id="match-quantity-modal"
        confirmButtonText={t("done")}
        loading={loading.includes("matchQuantity")}
        title={t("match_quantity")}
        hideModal={hideFormModal}
        fitContent
        bodyScroll={false}
        style={{ marginTop: "1.6rem" }}
        onSubmit={() => {}}
        confirmClick={handleClose}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            width: "100%",
            minWidth: 0,
            maxHeight: "calc(90vh - 240px)",
            overflow: "hidden",
          }}
        >
          <div style={{ flexShrink: 0 }}>
            <FormItem label={t("store")}>
              <Select
                style={{ width: "100%" }}
                value={selectedStoreId}
                onChange={handleStoreChange}
                options={quantities.map((q) => ({
                  value: q.store_id,
                  label: getStoreName(q.store_name),
                }))}
              />
            </FormItem>
          </div>

          {storeQuantity && (
            <div style={{ flexShrink: 0 }}>
              <Descriptions size="small" bordered column={3}>
                <Descriptions.Item label={t("entered_quantity")}>
                  {storeQuantity.entered_quantity}
                </Descriptions.Item>
                <Descriptions.Item label={t("withdrawn_quantity")}>
                  {Math.abs(storeQuantity.withdrawn_quantity)}
                </Descriptions.Item>
                <Descriptions.Item label={t("available")}>
                  {storeQuantity.quantity}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}

          {selectedStoreId &&
            (loadingBatches ? (
              <Row justify="center" style={{ flexShrink: 0 }}>
                <Spin />
              </Row>
            ) : (
              <div ref={tableWrapRef} style={{ flex: 1, minHeight: 200, overflow: "hidden" }}>
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={rows}
                  columns={columns}
                  pagination={{
                    current: page,
                    pageSize: perPage,
                    total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showLessItems: true,
                    showTotal: (tot, range) => `${range[0]}-${range[1]} / ${tot}`,
                    onChange: (nextPage, nextPageSize) => {
                      const normalizedPageSize = nextPageSize ?? perPage;
                      setPerPage(normalizedPageSize);
                      setPage(nextPage);
                      loadBatches({ page: nextPage, perPage: normalizedPageSize });
                    },
                  }}
                  scroll={{ x: 1100, y: tableScrollY }}
                  locale={{ emptyText: t("no_batches") }}
                />
              </div>
            ))}
        </div>
      </FormHookModal>
    );
  }, [passedId, quantities, loading]);

  return (
    <AntdButton
      type="primary"
      icon={<RiFileEditLine />}
      onClick={showFormModal}
      {...props}
    />
  );
};

export default MatchQuantityButton;
